import React from 'react';
import { ProfitInsight } from '../../types/insights';
import './InsightCard.css';

interface InsightCardProps {
  insight: ProfitInsight;
}

const InsightCard: React.FC<InsightCardProps> = ({ insight }) => {
  // Get icon and color based on insight type
  const getTypeIcon = (type: string): string => {
    switch (type) {
      case 'opportunity':
        return '🎯';
      case 'warning':
        return '⚠️';
      case 'pattern':
        return '📊';
      default:
        return '📌';
    }
  };

  const getTypeClass = (type: string): string => {
    switch (type) {
      case 'opportunity':
        return 'insight-card-opportunity';
      case 'warning':
        return 'insight-card-warning';
      case 'pattern':
        return 'insight-card-pattern';
      default:
        return '';
    }
  };

  const getPriorityClass = (priority: string): string => {
    switch (priority) {
      case 'high':
        return 'priority-high';
      case 'medium':
        return 'priority-medium';
      case 'low':
        return 'priority-low';
      default:
        return '';
    }
  };

  const formatImpact = (): string => {
    const { amount, period, direction } = insight.estimatedImpact;
    
    if (amount === 0) {
      return 'Qualitative benefit';
    }

    const formattedAmount = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Math.abs(amount));

    const periodStr = period === 'day' ? '/day' : period === 'week' ? '/week' : '/month';
    
    if (direction === 'gain') {
      return `+${formattedAmount}${periodStr} potential`;
    } else if (direction === 'savings') {
      return `${formattedAmount}${periodStr} savings`;
    } else {
      return `${formattedAmount}${periodStr} at risk`;
    }
  };

  const getTypeLabel = (type: string): string => {
    switch (type) {
      case 'opportunity':
        return 'Opportunity';
      case 'warning':
        return 'Warning';
      case 'pattern':
        return 'Pattern';
      default:
        return type;
    }
  };

  return (
    <div 
      className={`insight-card ${getTypeClass(insight.type)} ${getPriorityClass(insight.priority)}`}
      role="article"
      aria-label={`${getTypeLabel(insight.type)} insight: ${insight.title}`}
    >
      <div className="insight-card-header">
        <div className="insight-card-badge">
          <span className="insight-icon" aria-hidden="true">{getTypeIcon(insight.type)}</span>
          <span className="insight-type-label">{getTypeLabel(insight.type)}</span>
        </div>
        {insight.priority === 'high' && (
          <span className="priority-badge" aria-label="High priority">High Priority</span>
        )}
      </div>
      
      <h3 className="insight-card-title">{insight.title}</h3>
      
      <p className="insight-card-description">{insight.description}</p>
      
      <div className="insight-card-footer">
        <div className="insight-impact">
          <span className="impact-label">Estimated Impact:</span>
          <span className="impact-value">{formatImpact()}</span>
        </div>
        {insight.confidenceScore !== undefined && insight.confidenceScore < 0.7 && (
          <div className="confidence-note" aria-label={`Confidence score: ${Math.round(insight.confidenceScore * 100)}%`}>
            <span className="confidence-icon" aria-hidden="true">ℹ️</span>
            <span className="confidence-text">Limited data</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default InsightCard;
